import { normalizeError } from 'core/common/errors';
import {
  ATTRS_WEB_VITALS_NAME,
  ATTRS_WEB_VITALS_PATHNAME,
  ATTRS_WEB_VITALS_VALUE,
  SpanName,
} from 'core/common/observability/entities';
import { ObservabilitySystem } from 'core/common/observability/interfaces';
import { WebVitalsMetric } from '../entities';
import { WebVitalsRecorder } from '../interfaces';

export class WebVitalsObservabilityRecorder implements WebVitalsRecorder {
  private readonly observabilitySystem: ObservabilitySystem;

  constructor(observabilitySystem: ObservabilitySystem) {
    this.observabilitySystem = observabilitySystem;
  }

  record({ metric, pathname }: { metric: WebVitalsMetric; pathname: string }) {
    const span = this.observabilitySystem.startSpan(SpanName.TRACK_WEB_VITALS, { startTime: 0 });
    span.addEvent('Pending');

    try {
      const defaultAttributes = {
        [ATTRS_WEB_VITALS_NAME]: metric.name,
        [ATTRS_WEB_VITALS_PATHNAME]: pathname,
      };
      const specificAttributes = this.prepareAttributes(metric);

      span.setAttributes({ ...specificAttributes, ...defaultAttributes });

      span.addEvent('Succeed');
      span.succeed(specificAttributes[ATTRS_WEB_VITALS_VALUE]);
    } catch (e) {
      const err = normalizeError(e);
      span.addEvent('Failed');
      span.fail(err, 0);
    }
  }

  private prepareAttributes(metric: WebVitalsMetric) {
    switch (metric.name) {
      case 'LCP': {
        // Round to 1 decimal
        const roundedValue = Math.round(metric.value * 10) / 10;
        return {
          [ATTRS_WEB_VITALS_VALUE]: roundedValue,
        };
      }

      case 'INP': {
        // Round to the nearest whole number
        const roundedValue = Math.round(metric.value);
        return {
          [ATTRS_WEB_VITALS_VALUE]: roundedValue,
        };
      }

      case 'CLS': {
        // Round to 3 decimals
        const roundedValue = Math.round(metric.value * 1000 * 1000) / 1000;
        return {
          [ATTRS_WEB_VITALS_VALUE]: roundedValue,
        };
      }

      default: {
        // Default behavior, no specific rounding
        return {
          [ATTRS_WEB_VITALS_VALUE]: metric.value,
        };
      }
    }
  }
}
